import type { FC } from 'react';
import { memo, useEffect } from 'react';

import { FormattedMessage } from 'react-intl'

import { ImportAPI } from '@webapp/api/ImportAPI'
import { FormattedEnhancedMessage } from '@webapp/core/hoc/FormattedEnhancedMessage'
import { type ImportType } from '@webapp/models/enums'
import messages from '../../messages'
import { ImporterLoadingStage } from '../ImporterLoadingStage/ImporterLoadingStage'

const STATUS = {
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
}

export interface IImporterProcessingStageProps {
  onError?: (errCsvUrl, rowsErrored) => void
  onSuccess?: (rowsCreated) => void
  csvUUID?: string
  type?: ImportType
}

export const ImporterProcessingStage: FC<IImporterProcessingStageProps> =
  memo(({ csvUUID, onError, onSuccess, type }) => {
    useEffect(() => {
      checkImportStatus()
    }, [])

    let checkStatusTimeout = null

    const pollingInterval = 3000

    const getData = (res) => res?.data

    const getStatus = (res) => getData(res)?.status

    const getErrorCount = (res) => getData(res)?.erred

    const hasError = (res) => getErrorCount(res) > 0

    const cancelImportStatusCheck = () => {
      if (checkStatusTimeout) {
        clearTimeout(checkStatusTimeout)
      }
    }

    const checkImportStatus = (retries = 5) => {
      if (!csvUUID) return

      ImportAPI.getCSVImport(type, csvUUID)
        .then((res) => {
          const status = getStatus(res)

          if (status === STATUS.PROCESSING) {
            checkStatusTimeout = setTimeout(() => {
              checkImportStatus()
            }, pollingInterval)
            return
          } else if (status === STATUS.COMPLETED && hasError(res)) {
            if (onError) {
              cancelImportStatusCheck()
              return onError(getData(res).file, getData(res).erred)
            }
          } else if (status === STATUS.COMPLETED && !hasError(res)) {
            if (onSuccess) {
              cancelImportStatusCheck()
              return onSuccess(getData(res))
            }
          }
        })
        .catch((err) => {
          if (err.status === 404 && retries) {
            checkStatusTimeout = setTimeout(() => {
              checkImportStatus(retries - 1)
            }, pollingInterval)
          } else {
            cancelImportStatusCheck()
          }
        })
    }

    return (
      <ImporterLoadingStage
        title={<FormattedMessage {...messages.processingStepTitle} />}
        message={
          <FormattedEnhancedMessage {...messages.processingStepDescription} />
        }
      />
    )
  })
